import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Link,
  useNavigate,
  useOutletContext,
  useSearchParams,
} from 'react-router-dom'
import { LoadingButton } from '@mui/lab'
import { Box, Button, Container, Grid, Paper, Typography } from '@mui/material'
import { styled } from '@mui/material/styles'
import { find, isEmpty } from 'lodash'

import { CarDictionaries, DigitalCertificate } from '../api/index'
import { REACT_APP_DOMAIN } from '../common/constants'
import ButtonBox from '../components/ButtonBox'
import CarDataTable from '../components/CarDataTable'
import Section from '../components/Section'
import SectionHeader from '../components/SectionHeader'
import SelectableCard from '../components/SelectableCard'
import useAlert from '../hooks/useAlert'
import useAuth from '../hooks/useAuth'
import useAxiosPrivate from '../hooks/useAxiosPrivate'
import { PrinterBigIcon, TabletIcon } from '../icons/index'

const StyledPaper = styled((props) => <Paper {...props} elevation={0} />)({
  height: `100%`,
})

function ControlNewType() {
  const { car, setCar } = useOutletContext()
  const {
    auth: { refactory, dealerId },
  } = useAuth()
  const axiosPrivate = useAxiosPrivate()
  const navigate = useNavigate()
  const { setAlert } = useAlert()
  const { t } = useTranslation(['common', 'create'])
  const [params] = useSearchParams()
  const paramsVin = params.get('vin')
  const isRefactory = refactory === 'true'

  const [typesOpts, setTypesOpts] = useState([])
  const [makesOpts, setMakesOpts] = useState([])
  const [modelsOpts, setModelsOpts] = useState([])
  const [versionsOpts, setVersionsOpts] = useState([])
  const [isSelected, setIsSelected] = useState(false)
  const [isBtnLoading, setIsBtnLoading] = useState(false)
  const [isImgBtnLoading, setIsImgBtnLoading] = useState(false)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const digitalCertificateId = sessionStorage.getItem('digitalCertificateId')

    const loadData = async () => {
      if (digitalCertificateId) {
        if (isEmpty(car)) {
          await fetchInitData()
        } else {
          await fetchData(car.typeId, car.makeId, car.modelId)
        }
      } else {
        await fetchTypes()
        if (paramsVin) {
          setCar((prevCar) => ({
            ...prevCar,
            vin: paramsVin,
          }))
        }
      }
      setLoading(false)
    }

    loadData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const fetchInitData = async () => {
    const digitalCertificateId = sessionStorage.getItem('digitalCertificateId')
    try {
      const { data } = await DigitalCertificate.getCertificate(
        axiosPrivate,
        digitalCertificateId,
      )

      setCar({ ...data })
      await fetchData(data.typeId, data.makeId, data.modelId)
    } catch (error) {
      // eslint-disable-next-line no-shadow, no-console
      console.error('Error fetching data:', error)
    }
  }

  const fetchData = async (typeId, makeId, modelId) => {
    await fetchTypes()
    if (typeId) await fetchMakes(typeId)
    if (makeId) await fetchModels(typeId, makeId)
    if (modelId) await fetchVersions(modelId)
  }

  const fetchTypes = async () => {
    try {
      const { data: typesData } = await CarDictionaries.getTypesDictionary(
        axiosPrivate,
      )
      setTypesOpts(typesData)
    } catch (error) {
      // eslint-disable-next-line no-shadow, no-console
      console.error('Error fetching data:', error)
    }
  }

  const fetchMakes = async (typeId) => {
    try {
      const { data: makesData } = await CarDictionaries.getMakesDictionary(
        axiosPrivate,
        typeId,
      )
      setMakesOpts(makesData)
    } catch (error) {
      // eslint-disable-next-line no-shadow, no-console
      console.error('Error fetching data:', error)
    }
  }

  const fetchModels = async (typeId, makeId) => {
    try {
      const { data: modelsData } = await CarDictionaries.getModelsDictionary(
        axiosPrivate,
        typeId,
        makeId,
      )
      setModelsOpts(modelsData)
    } catch (error) {
      // eslint-disable-next-line no-shadow, no-console
      console.error('Error fetching data:', error)
    }
  }

  const fetchVersions = async (modelId) => {
    try {
      const { data: versionsData } =
        await CarDictionaries.getVersionsDictionary(axiosPrivate, modelId)
      setVersionsOpts(versionsData)
    } catch (error) {
      // eslint-disable-next-line no-shadow, no-console
      console.error('Error fetching data:', error)
    }
  }

  const handleSelectVariant = (e) => {
    e.preventDefault()
    setIsSelected((prevState) => !prevState)
  }

  const getPDF = () => {
    window.open(
      `${REACT_APP_DOMAIN}/Content/Files/CERTYFIKAT_KONTROLI_SAMOCHODU_UZYWANEGO.pdf`,
      '_blank',
    )
  }

  const handleInputChange = (name, value) => {
    if (name === 'typeId') {
      fetchMakes(value)
      setCar({
        ...car,
        typeId: value,
        makeId: 0,
        modelId: 0,
        ecCode: 0,
      })
    } else if (name === 'makeId') {
      fetchModels(car.typeId, value)
      setCar({ ...car, makeId: value, modelId: 0, ecCode: 0 })
    } else if (name === 'modelId') {
      if (!isRefactory) {
        fetchVersions(value)
      }
      setCar({ ...car, modelId: value, ecCode: 0 })
    } else {
      setCar({ ...car, [name]: value })
    }
  }

  const handleImageChange = (e) => {
    const fileForm = new FormData()
    fileForm.append('file', e.target.files[0])
    setIsImgBtnLoading(true)
    DigitalCertificate.setCertificateImage(axiosPrivate, fileForm)
      .then(({ data: { message } }) => {
        setIsImgBtnLoading(false)
        setCar({ ...car, imageUrl: message })
      })
      // eslint-disable-next-line no-shadow, no-console
      .catch((e) => console.error(e))
  }

  const createNewControl = () => {
    setIsBtnLoading(true)
    const payload = {
      ...car,
      sourceDealerId: dealerId,
      ...(car?.source?.type === 'Manual' ||
        (!car?.source && {
          source: {
            type: 'Manual',
          },
          dealerId,
          type: find(typesOpts, (type) => type.id === car.typeId)?.name,
          make: find(makesOpts, (make) => make.id === car.makeId)?.name,
          model: find(modelsOpts, (model) => model.id === car.modelId)?.name,
          fuel: find(versionsOpts, (version) => version.id === car.ecCode)
            ?.fuel,
          version: find(versionsOpts, (version) => version.id === car.ecCode)
            ?.name,
        })),
    }

    DigitalCertificate.createCerificate(axiosPrivate, payload, dealerId)
      .then(({ data: { data: digitalCertificateId } }) => {
        setAlert(t('create:car.form.success'), 'success')
        sessionStorage.setItem('digitalCertificateId', digitalCertificateId)
        navigate(`/controls/${digitalCertificateId}/edit`)
      })
      .catch(({ response }) => {
        const { status, data } = response
        const { errors, message: defaultMessage } = data

        if (status === 409) {
          setAlert(t('create:car.error.alreadyExist', { vin: car.vin }), 'info')
          sessionStorage.setItem('digitalCertificateId', data.data)
          navigate(`/controls/${data.data}/edit`)
          return false
        }

        if (errors?.[0]) {
          const { message: errorMessage, field: errorField } = errors[0]
          const fieldLabel = t(
            `create:car.form.input.${errorField.toLowerCase()}.label`,
          )
          setAlert(`${errorMessage}: ${fieldLabel}`, 'error')
        } else {
          setAlert(`Error ${status}: ${defaultMessage}`, 'error')
        }

        return false
      })
      .finally(() => {
        setIsBtnLoading(false)
      })
  }

  const manualTypeFields = [
    {
      name: 'typeId',
      label: t('create:car.form.input.type.label'),
      value: car?.typeId,
      options: typesOpts,
      isEditable: true,
      isSelect: true,
      disabled: loading,
    },
    {
      name: 'makeId',
      label: t('create:car.form.input.make.label'),
      value: car?.makeId,
      options: makesOpts,
      isEditable: true,
      isSelect: true,
      disabled: loading || !car?.typeId,
    },
    {
      name: 'modelId',
      label: t('create:car.form.input.model.label'),
      value: car?.modelId,
      options: modelsOpts,
      isEditable: true,
      isSelect: true,
      disabled: loading || !car?.makeId,
    },
    ...(!isRefactory
      ? [
          {
            name: 'ecCode',
            label: t('create:car.form.input.version.label'),
            value: car?.ecCode,
            options: versionsOpts,
            isEditable: true,
            isSelect: true,
            disabled: loading || !car?.modelId,
          },
        ]
      : []),
  ]

  const commonFields = [
    {
      name: 'vin',
      label: t('create:car.form.input.vin.label'),
      value: car.vin,
      isEditable: true,
    },
    {
      name: 'registrationNumber',
      label: t('create:car.form.input.regNo.label'),
      value: car.registrationNumber,
      isEditable: true,
    },
    {
      name: 'firstRegistrationDate',
      label: t('create:car.form.input.firstRegistrationDate.label'),
      value: car.firstRegistrationDate,
      isEditable: true,
      isDate: true,
    },
  ]

  const additionalFields = [
    {
      name: 'mileage',
      label: t('create:car.form.input.mileage.label'),
      value: car.mileage,
      isEditable: true,
    },
    {
      name: 'color',
      label: t('create:car.form.input.color.label'),
      value: car.color,
      isEditable: true,
    },
    {
      name: 'manufacturingYear',
      label: t('create:car.form.input.manufacturingYear.label'),
      value: car.manufacturingYear,
      isEditable: true,
    },
  ]

  const carDataTable = [
    ...commonFields,
    ...(['Manual', undefined].includes(car?.source?.type)
      ? manualTypeFields
      : []),
    ...(isRefactory ? [] : additionalFields),
  ]

  return (
    <Container maxWidth="xl">
      <Section>
        <SectionHeader
          title={t('create:car.title')}
          subtitle={t('create:car.subtitle')}
        />
        <Grid container spacing={5}>
          {!isRefactory && (
            <Grid item xs={12} md={6}>
              <StyledPaper>
                <Box
                  sx={{
                    padding: 3,
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  <Typography variant="h3" sx={{ mb: 4 }}>
                    {!car?.source?.id ? (
                      t('create:car.carNamePlaceholder')
                    ) : (
                      <>
                        <strong>
                          {car.make} {car.model}&nbsp;
                        </strong>
                        <Typography variant="h5" component="span">
                          {car.version}
                        </Typography>
                      </>
                    )}
                  </Typography>
                  <Box
                    sx={{
                      position: 'relative',
                      width: '100%',
                      m: '0 auto',
                    }}
                  >
                    {car.imageUrl && (
                      <img
                        src={car.imageUrl}
                        alt={`${car.make} ${car.model} ${car.version}`}
                        style={{ width: '100%' }}
                      />
                    )}
                    {isImgBtnLoading ? (
                      <LoadingButton
                        variant="contained"
                        color="primary"
                        loading={isImgBtnLoading}
                        sx={({ spacing }) => ({
                          position: 'absolute',
                          top: spacing(2),
                          right: spacing(2),
                          py: {
                            xs: 2,
                            md: 2,
                            lg: 2,
                          },
                          px: {
                            xs: 6,
                            md: 6,
                            lg: 6,
                          },
                          '&.Mui-disabled': {
                            backgroundColor: '#dcdcdc',
                          },
                        })}
                      />
                    ) : (
                      <Button
                        size="small"
                        component="label"
                        variant="contained"
                        color="primary"
                        sx={({ spacing }) => ({
                          position: 'absolute',
                          top: spacing(2),
                          right: spacing(2),
                          borderRadius: 0,
                          textAlign: 'center',
                        })}
                      >
                        <input
                          name="image"
                          hidden
                          accept="image/*"
                          multiple
                          type="file"
                          onChange={handleImageChange}
                          style={{
                            display: 'none',
                          }}
                        />
                        {car.imageUrl
                          ? t('create:car.form.input.changePhoto.label')
                          : t('create:car.form.input.addPhoto.label')}
                      </Button>
                    )}
                  </Box>
                </Box>
              </StyledPaper>
            </Grid>
          )}
          <Grid item xs={12} md={6}>
            <StyledPaper>
              <CarDataTable
                onInputChange={handleInputChange}
                data={carDataTable}
                isRefactory={isRefactory}
              />
            </StyledPaper>
          </Grid>
        </Grid>
      </Section>
      <Section>
        <SectionHeader
          title={t('create:type.title')}
          subtitle={t('create:type.subtitle')}
        />
        <Grid container spacing={5}>
          <Grid item xs={12} md={6}>
            <SelectableCard
              onClick={handleSelectVariant}
              selected={isSelected}
              icon={<TabletIcon />}
              id="online"
              title={t('create:type.button.online.title')}
              subtitle={t('create:type.button.online.subtitle')}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <SelectableCard
              icon={<PrinterBigIcon />}
              id="print"
              title={t('create:type.button.print.title')}
              subtitle={t('create:type.button.print.subtitle')}
              onClick={getPDF}
            />
          </Grid>
        </Grid>
      </Section>
      <ButtonBox
        mt={2}
        buttons={[
          {
            label: t('button.back'),
            props: {
              variant: 'outlined',
              color: 'primary',
              component: Link,
              to: '/controls/new/car',
            },
          },
          {
            label: t('create:button.start'),
            props: {
              variant: 'contained',
              color: 'primary',
              loading: isBtnLoading,
              onClick: createNewControl,
              disabled: !isSelected,
            },
          },
        ]}
      />
    </Container>
  )
}

export default ControlNewType
